import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import LoginForm from 'components/auth/portal/LoginForm';
import RegisterForm from 'components/auth/portal/RegisterForm';
import { homePathByRole } from 'common/constants/general';
import routes from 'common/routes';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Panel del slider (md+): ambos formularios viven montados y deslizan con
// transform/opacity; el inactivo queda oculto e inerte (visibility diferida para
// que el saliente alcance a desvanecerse). Cada panel scrollea por dentro.
const paneSx = (active, direction) => ({
  position: 'absolute',
  inset: 0,
  overflowY: 'auto',
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'safe center',
  opacity: active ? 1 : 0,
  transform: active ? 'none' : `translateY(${direction === 'up' ? -28 : 28}px)`,
  visibility: active ? 'visible' : 'hidden',
  pointerEvents: active ? 'auto' : 'none',
  transition: `opacity 450ms ${EASE}, transform 450ms ${EASE}, visibility 0s linear ${active ? 0 : 450}ms`,
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    transform: 'none'
  }
});

// Animación de entrada del panel único en mobile (no hay altura fija para deslizar dos).
const mobilePaneSx = {
  p: 2.5,
  '@keyframes authPaneIn': {
    from: { opacity: 0, transform: 'translateY(16px)' },
    to: { opacity: 1, transform: 'none' }
  },
  animation: 'authPaneIn 240ms cubic-bezier(0.32, 0.72, 0, 1)',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
};

/**
 * Portal de acceso: UNA pantalla registrada en '/', '/login' y '/register' (las URLs
 * se conservan). Comparte boundary key en el router, así que NO remonta al navegar
 * entre esas rutas: la vista sale de useLocation y el cambio Ingresar ↔ Crear cuenta
 * es un deslizamiento suave. Back/forward del navegador también animan.
 */
function AuthPortal() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['routes', 'common']);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const hasNavigated = useRef(false);

  const user = useSelector((state) => state.auth.user);

  const isSignUp = pathname === routes.register.path;
  const pageTitle = isSignUp
    ? t('auth.createAccount', { ns: 'common', defaultValue: 'Crear cuenta' })
    : t('login', { ns: 'routes' });

  // Post-login: al home del rol.
  //
  // En MÓVIL se ignora a propósito el `?redirect=`: entrar directamente a una pantalla profunda
  // deja al usuario sin contexto de dónde está, y era el camino por el que la navegación acababa
  // en estados raros. Empezar siempre por el inicio es lo que se pidió y además es lo que espera
  // cualquiera al iniciar sesión desde el teléfono.
  //
  // En escritorio sí se respeta, que es donde tiene sentido: es lo que hace que un enlace de correo
  // a una factura lleve a la factura después de identificarse.
  useEffect(() => {
    if (user && !hasNavigated.current) {
      hasNavigated.current = true;
      const home = homePathByRole[user?.role] || homePathByRole.default;
      const redirect = searchParams.get('redirect');
      const safeRedirect = isDesktop && redirect && redirect.startsWith('/') ? redirect : null;
      navigate(safeRedirect || home, { replace: true });
    }
  }, [navigate, user, searchParams, isDesktop]);

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      {isDesktop ? (
        <>
          <Box sx={paneSx(!isSignUp, 'up')} aria-hidden={isSignUp}>
            <LoginForm />
          </Box>
          <Box sx={paneSx(isSignUp, 'down')} aria-hidden={!isSignUp}>
            <RegisterForm />
          </Box>
        </>
      ) : (
        <Box key={isSignUp ? 'signup' : 'signin'} sx={mobilePaneSx}>
          {isSignUp ? <RegisterForm /> : <LoginForm />}
        </Box>
      )}
    </>
  );
}

export default AuthPortal;
